import { useEffect, useState } from 'react'
import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTranslation } from 'react-i18next'

/** Far enough down that the buttons are answering a need rather than announcing themselves. */
const SHOW_AFTER = 400

/** Within this of the end, "go to the bottom" has nothing left to do. */
const NEAR_BOTTOM = 120

/**
 * Jump to the top of the page, or to the bottom of it.
 *
 * <p>The cottage pages run long - gallery, amenities, a three-month calendar, then the booking
 * form - and on a phone the way back to the menu is a lot of thumb. Both buttons only appear
 * once there is somewhere to go: at the top of a short page neither is drawn at all.
 *
 * <p>They sit above the review widget rather than beside it, stacked on the same edge, and use
 * {@code insetInlineEnd} so Hebrew puts them on the left without a second rule.
 */
export function ScrollButtons() {
  const { t } = useTranslation()
  const [scrolled, setScrolled] = useState(false)
  const [moreBelow, setMoreBelow] = useState(false)

  useEffect(() => {
    const update = () => {
      const y = window.scrollY
      const remaining = document.documentElement.scrollHeight - y - window.innerHeight
      setScrolled(y > SHOW_AFTER)
      setMoreBelow(remaining > NEAR_BOTTOM)
    }
    update()
    // Passive: this listener never calls preventDefault, and saying so lets the browser
    // scroll without waiting to find out.
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  const goTo = (top: number) => window.scrollTo({ top, behavior: 'smooth' })

  if (!scrolled && !moreBelow) return null

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        // Clear of the review widget, which owns the bottom of this edge.
        bottom: { xs: 88, md: 96 },
        insetInlineEnd: 24,
        zIndex: 1200,
      }}
    >
      {scrolled && (
        <Tooltip title={t('common.backToTop')} placement="left">
          <Fab
            size="small"
            color="default"
            aria-label={t('common.backToTop')}
            onClick={() => goTo(0)}
            sx={{ bgcolor: 'background.paper', boxShadow: 3 }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Tooltip>
      )}
      {moreBelow && (
        <Tooltip title={t('common.toTheBottom')} placement="left">
          <Fab
            size="small"
            color="default"
            aria-label={t('common.toTheBottom')}
            onClick={() => goTo(document.documentElement.scrollHeight)}
            sx={{ bgcolor: 'background.paper', boxShadow: 3 }}
          >
            <KeyboardArrowDownIcon />
          </Fab>
        </Tooltip>
      )}
    </Stack>
  )
}
